import React from "react";
import { Instagram } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import {
  StandardizedTable,
  type TableColumn,
  type TableRow,
} from "@/components/ui/standardized-table";
import type { TableQueryDTO } from "@/types/table-query";
import type {
  InstagramPostRow,
  InstagramPostsApiResponse,
} from "./instagram.types";

interface InstagramPostsTimelineProps {
  data: InstagramPostRow[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
  };
  totalRow?: InstagramPostsApiResponse["total_row"] | null;
  onQueryChange: (query: TableQueryDTO) => void;
  loading?: boolean;
}

function formatPostDateTime(value: unknown): string {
  if (typeof value !== "string" || value.trim() === "") {
    return "-";
  }

  const rawValue = value.trim();
  const dateOnlyMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return `${day}/${month}/${year}`;
  }

  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) {
    return rawValue;
  }

  return parsed.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function PostThumbnailCell({
  src,
  alt,
}: {
  src: string | null;
  alt: string;
}): React.ReactElement {
  const [hasError, setHasError] = React.useState(false);
  const validSrc = typeof src === "string" && src.trim() !== "" ? src : null;
  const showImage = !!validSrc && !hasError;

  if (showImage) {
    return (
      <img
        src={validSrc}
        alt={alt}
        className="size-9 rounded-lg object-cover ring-1 ring-border"
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setHasError(true)}
      />
    );
  }

  return (
    <div className="size-9 rounded-lg bg-muted ring-1 ring-border flex items-center justify-center">
      <Instagram className="h-4 w-4 text-muted-foreground" />
    </div>
  );
}

/** Column definitions for the posts timeline table */
const POSTS_COLUMNS: TableColumn[] = [
  {
    key: "thumbnail",
    header: "Img.",
    align: "left",
    sortable: true,
    width: 50,
    render: (
      value: unknown,
      item: TableRow,
      _index: number,
      isTotal?: boolean,
    ) => {
      if (isTotal) return null;
      const thumbnailUrl = typeof value === "string" ? value : null;
      const captionText =
        typeof item.caption === "string"
          ? item.caption
          : "Publicação do Instagram";
      return <PostThumbnailCell src={thumbnailUrl} alt={captionText} />;
    },
  },
  {
    key: "mediaType",
    header: "Tipo de Mídia",
    align: "left",
    width: 120,
    maxWidth: 120,
    sortable: true,
    render: (
      value: string,
      _item: TableRow,
      _index: number,
      isTotal?: boolean,
    ) => {
      if (isTotal) return null;
      return (
        <Badge
          variant={value === "reel" ? "default" : "secondary"}
          className="text-[10px]"
        >
          {value === "reel"
            ? "Reels"
            : value === "carousel"
              ? "Carousel"
              : "Post"}
        </Badge>
      );
    },
  },
  {
    key: "date",
    header: "Data/Hora",
    align: "left",
    type: "text",
    width: 180,
    maxWidth: 180,
    sortable: true,
    render: (
      value: unknown,
      _item: TableRow,
      _index: number,
      isTotal?: boolean,
    ) => {
      if (isTotal) return null;
      const formattedDateTime = formatPostDateTime(value);
      return (
        <span className="block truncate" title={formattedDateTime}>
          {formattedDateTime}
        </span>
      );
    },
  },
  {
    key: "caption",
    header: "Legenda",
    align: "left",
    type: "text",
    width: 140,
    maxWidth: 140,
    sortable: true,
    render: (
      value: string,
      _item: TableRow,
      _index: number,
      isTotal?: boolean,
    ) => {
      const fullText = String(value ?? "");
      if (isTotal) {
        return <span className="font-semibold">{fullText}</span>;
      }
      return (
        <span className="block truncate" title={fullText}>
          {fullText}
        </span>
      );
    },
  },
  {
    key: "reach",
    header: "Alcance",
    align: "right",
    type: "number",
    sortable: true,
  },
  {
    key: "views",
    header: "Visualizações",
    align: "right",
    type: "number",
    sortable: true,
  },
  {
    key: "saves",
    header: "Salvar",
    align: "right",
    type: "number",
    sortable: true,
  },
  {
    key: "likes",
    header: "Curtidas",
    align: "right",
    type: "number",
    sortable: true,
  },
  {
    key: "comments",
    header: "Comentários",
    align: "right",
    type: "number",
    sortable: true,
  },
  {
    key: "shares",
    header: "Compart.",
    align: "right",
    type: "number",
    sortable: true,
  },
  {
    key: "interactions",
    header: "Interações",
    align: "right",
    type: "number",
    sortable: true,
  },
  {
    key: "interactionRate",
    header: "TX. Interação",
    align: "right",
    type: "percent",
    sortable: true,
  },
];

/** Skeleton placeholder for the posts timeline section */
function PostsTimelineSkeleton(): React.ReactElement {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-64" />
      </CardHeader>
      <CardContent className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-4 w-48" />
      </CardContent>
    </Card>
  );
}

/** Transform InstagramPostRow[] into StandardizedTable-compatible TableRow[] */
function toTableRows(posts: InstagramPostRow[]): TableRow[] {
  return posts.map((p) => ({
    id: p.id,
    thumbnail: p.thumbnail,
    mediaType: p.mediaType,
    date: p.date,
    caption: p.caption,
    reach: p.reach,
    views: p.views,
    saves: p.saves,
    likes: p.likes,
    comments: p.comments,
    shares: p.shares,
    interactions: p.interactions,
    interactionRate: p.interactionRate,
  }));
}

function mapTotalRow(
  totalRow?: InstagramPostsApiResponse["total_row"] | null,
): Record<string, string | number> | undefined {
  if (!totalRow) return undefined;

  return {
    thumbnail: "",
    mediaType: "",
    date: "",
    caption: "Total Geral",
    reach: Number(totalRow.reach || 0),
    views: Number(totalRow.views || 0),
    saves: Number(totalRow.saves || 0),
    likes: Number(totalRow.likes || 0),
    comments: Number(totalRow.comments || 0),
    shares: Number(totalRow.shares || 0),
    interactions: Number(totalRow.interactions || 0),
    interactionRate: totalRow.interactionRate ?? "-",
  };
}

export default function InstagramPostsTimeline({
  data,
  pagination,
  totalRow,
  onQueryChange,
  loading = false,
}: InstagramPostsTimelineProps): React.ReactElement {
  if (loading && data.length === 0) return <PostsTimelineSkeleton />;

  return (
    <StandardizedTable
      title={
        <span className="inline-flex items-center gap-2">
          Postagens Timeline (Dados Orgânicos)
          <HelpTooltip
            content="Resumo detalhado das postagens orgânicas no período"
            triggerClassName="h-3.5 w-3.5 text-[9px]"
          />
        </span>
      }
      columns={POSTS_COLUMNS}
      data={toTableRows(data)}
      total={mapTotalRow(totalRow)}
      queryMode="server"
      onQueryChange={onQueryChange}
      pagination={pagination}
      loading={loading}
    />
  );
}
